import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Check } from "lucide-react";
import { BRAND, SERVICES } from "../mock";
import InvestorForm from "../components/InvestorForm";

function ServiceIcon({ name, className }) {
  const Cmp = Icons[name] || Icons.Building2;
  return <Cmp className={className} />;
}

function Reveal({ children, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && el.classList.add("in-view"),
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} className={`fade-up ${className}`}>{children}</div>;
}

export default function Services() {
  // Land & Investment is moved to the end so its investor form sits at the
  // bottom of the page without interrupting the flow of the other services.
  const orderedServices = [
    ...SERVICES.filter((s) => s.id !== "land-investment"),
    ...SERVICES.filter((s) => s.id === "land-investment"),
  ];
  return (
    <div>
      {/* PAGE HERO */}
      <section className="brand-texture relative bg-[#1F3463] pb-20 pt-48">
        <div className="mx-auto max-w-7xl px-5 text-center lg:px-8">
          <span className="text-sm font-semibold uppercase tracking-widest text-[#C9A876]">
            Our Services
          </span>
          <h1 className="mt-3 font-display text-4xl font-bold text-white sm:text-5xl">
            Everything property, end to end
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-white/75">
            From the first plot of land to the final coat of paint and beyond —
            six specialist teams working as one to deliver lasting value.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {orderedServices.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="rounded-full border border-white/20 px-4 py-2 text-sm font-medium text-white/80 transition-colors hover:border-[#C9A876] hover:text-[#C9A876]"
              >
                {s.name}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE BLOCKS */}
      <div>
        {orderedServices.map((s, i) => {
          const reversed = i % 2 === 1;
          return (
            <React.Fragment key={s.id}>
            <section
              id={s.id}
              className={`scroll-mt-28 py-20 ${i % 2 === 1 ? "bg-[#F7F8FA]" : "bg-white"}`}
            >
              <div className="mx-auto max-w-7xl px-5 lg:px-8">
                <div className={`grid items-center gap-12 lg:grid-cols-2 ${reversed ? "lg:[&>*:first-child]:order-2" : ""}`}>
                  <Reveal>
                    <div className="img-zoom overflow-hidden rounded-2xl shadow-xl">
                      <img src={s.image} alt={s.name} className="h-[420px] w-full object-cover" />
                    </div>
                  </Reveal>
                  <Reveal>
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-[#F5F1E8] text-[#1F3463]">
                        <ServiceIcon name={s.icon} className="h-6 w-6" />
                      </span>
                      <span className="rounded-full bg-[#1F3463]/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-[#1F3463]">
                        {s.sector}
                      </span>
                    </div>
                    <h2 className="mt-5 font-display text-3xl font-bold text-[#1F3463] sm:text-4xl">
                      {s.name}
                    </h2>
                    <p className="mt-2 text-lg font-medium italic text-[#C9A876]">“{s.tagline}”</p>
                    {s.body.map((p, idx) => (
                      <p key={idx} className="mt-4 text-base leading-relaxed text-slate-600">
                        {p}
                      </p>
                    ))}
                    {s.bullets && (
                      <ul className="mt-5 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                        {s.bullets.map((b) => (
                          <li key={b} className="flex items-center gap-2 text-slate-700">
                            <Check className="h-5 w-5 shrink-0 text-[#C9A876]" />
                            <span className="font-medium">{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <Link
                      to="/contact"
                      className="btn-navy mt-7 inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold uppercase tracking-wide"
                    >
                      Enquire <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Reveal>
                </div>
              </div>
            </section>
            {s.id === "land-investment" && (
              <section
                id="investors"
                className="brand-texture scroll-mt-28 bg-[#1F3463] py-20"
              >
                <div className="mx-auto max-w-7xl px-5 lg:px-8">
                  <div className="grid items-center gap-12 lg:grid-cols-2">
                    <Reveal>
                      <span className="text-sm font-semibold uppercase tracking-widest text-[#C9A876]">
                        Investors
                      </span>
                      <h2 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl">
                        Invest in land &amp; housing with us
                      </h2>
                      <p className="mt-5 text-lg leading-relaxed text-white/75">
                        We're keen to work with trusted investors to support a
                        housing solution that helps young people build commitment,
                        build communities and get on the housing ladder.
                      </p>
                      <p className="mt-4 text-lg leading-relaxed text-white/75">
                        Whether you're interested in land acquisition,
                        Build-to-Rent, a joint venture or general investment,
                        register your interest and our team will be in touch to
                        explore the opportunity.
                      </p>
                      <ul className="mt-6 space-y-3">
                        {["Strategic land opportunities", "Build-to-Rent schemes", "Joint ventures & partnerships"].map((p) => (
                          <li key={p} className="flex items-center gap-2 text-white/90">
                            <Check className="h-5 w-5 text-[#C9A876]" /> {p}
                          </li>
                        ))}
                      </ul>
                    </Reveal>
                    <Reveal>
                      <InvestorForm />
                    </Reveal>
                  </div>
                </div>
              </section>
            )}
            </React.Fragment>
          );
        })}
      </div>

      {/* PARTNER OPPORTUNITIES */}
      <section className="brand-texture bg-[#1F3463] py-24">
        <div className="mx-auto max-w-4xl px-5 text-center lg:px-8">
          <span className="text-sm font-semibold uppercase tracking-widest text-[#C9A876]">
            Work With Us
          </span>
          <h2 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl">
            Partner Opportunities
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-white/75">
            We're keen to work with like-minded partners to make the most of land
            and property. Whether you're a landowner, investor or developer, we
            offer tailored opportunities to support growth and create sustainable
            communities.
          </p>
          <div className="mx-auto mt-8 flex max-w-md flex-wrap justify-center gap-4">
            {["Landowners", "Investors", "Developers"].map((p) => (
              <span key={p} className="flex items-center gap-2 text-white/90">
                <Check className="h-5 w-5 text-[#C9A876]" /> {p}
              </span>
            ))}
          </div>
          <Link
            to="/contact"
            className="btn-gold mt-9 inline-flex items-center gap-2 rounded-md px-8 py-4 text-sm font-semibold uppercase tracking-wide"
          >
            If you wish to get in touch <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
